// The tensor app's store: one immutable state object, replaced on every
// update (the same way as talk's src/state/store.ts).
import { useRef, useSyncExternalStore } from 'react'

import { type TState, initialState } from './model'

type Listener = () => void

let state: TState = initialState
const listeners = new Set<Listener>()

export function getState(): TState {
  return state
}

export function setState(update: (s: TState) => TState): void {
  const next = update(state)
  if (next === state) return
  state = next
  for (const l of listeners) l()
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Reads a slice of the state; cached per (state, selector). */
export function useT<T>(selector: (s: TState) => T): T {
  const cache = useRef<{ state: TState; selector: (s: TState) => T; value: T } | null>(null)
  const read = (): T => {
    const c = cache.current
    if (c && c.state === state && c.selector === selector) return c.value
    const value = selector(state)
    cache.current = { state, selector, value }
    return value
  }
  return useSyncExternalStore(subscribe, read, read)
}
